const DAY = 86_400_000

export function calendarDay(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const date = new Date(`${value}T00:00:00.000Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null
}

type LoanEstimateInput = {
  principalCents: number
  feeCents: number
  monthlyInterestBps: number
	monthlyInterestCents?: number
	interestStartsAt?: string
  compound: boolean
  issuedAt: string
  dueAt?: string
}

// Match daily ledger accrual: start the day after issue, use each calendar
// month's length, truncate to whole cents, and compound unpaid interest only.
export function estimateLoan(input: LoanEstimateInput) {
  const start = calendarDay(input.issuedAt)
	const interestStart = input.interestStartsAt ? calendarDay(input.interestStartsAt) : start
	const fixedAmount = input.monthlyInterestCents ?? 0
	if (!interestStart || !Number.isSafeInteger(fixedAmount) || fixedAmount < 0 || (fixedAmount > 0 && (input.monthlyInterestBps > 0 || input.compound))) return null
  if (!start || ![input.principalCents, input.feeCents, input.monthlyInterestBps].every(Number.isSafeInteger)
    || input.principalCents <= 0 || input.feeCents < 0 || input.monthlyInterestBps < 0 || input.monthlyInterestBps > 10000) return null
  const end = input.dueAt ? calendarDay(input.dueAt) : new Date(start.getTime() + 365 * DAY)
  if (!end || end < start) return null
  const days = Math.round((end.getTime() - start.getTime()) / DAY)
  // Bound preview work and reject values that cannot be represented in cents.
  if (days > 36600) return null
  const principal = BigInt(input.principalCents)
  const rate = BigInt(input.monthlyInterestBps)
  const fee = BigInt(input.feeCents)
  let interest = 0n
  for (const date = new Date(start.getTime() + DAY); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    const daysInMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()
    const base = principal + (input.compound ? interest : 0n)
		if (date > interestStart) {
			if (fixedAmount > 0) {
				const months = (date.getUTCFullYear() - interestStart.getUTCFullYear()) * 12 + date.getUTCMonth() - interestStart.getUTCMonth()
				if (months > 0 && date.getUTCDate() === Math.min(interestStart.getUTCDate(), daysInMonth)) interest += BigInt(fixedAmount)
			} else interest += base * rate / 10000n / BigInt(daysInMonth)
		}
    if (principal + fee + interest > BigInt(Number.MAX_SAFE_INTEGER)) return null
  }
  const total = principal + fee + interest
  if (total > BigInt(Number.MAX_SAFE_INTEGER)) return null
  return { days, interestCents: Number(interest), totalCents: Number(total), through: end.toISOString().slice(0, 10) }
}
